/**
 * /frontend/src/features/analytics/pages/AbandonmentAnalyticsPage.js
 */

import React, { useEffect, useMemo, useState } from 'react';
import useAbandonmentAnalytics from '../hooks/useAbandonmentAnalytics';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import GranularitySelector from '../components/shared/GranularitySelector';
import AbandonmentKPIs from '../components/abandonment/AbandonmentKPIs';
import AbandonmentTimingHistogram from '../components/abandonment/AbandonmentTimingHistogram';
import AbandonmentBySectionChart from '../components/abandonment/AbandonmentBySectionChart';
import AbandonmentTrendChart from '../components/abandonment/AbandonmentTrendChart';
import {
  toAbandonmentBySectionSeries,
  toAbandonmentHistogramSeries,
  toAbandonmentTrendSeries
} from '../utils/chartDataTransformers';
import './AbandonmentAnalyticsPage.css';

const GRANULARITIES = ['HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY'];

/**
 * Abandonment analytics page.
 * @param {{request:Function,storeToken:string,storeUuid:string,startDate:string,endDate:string,refreshSignal?:unknown,onSyncingChange?:Function}} props
 * @returns {JSX.Element}
 */
const AbandonmentAnalyticsPage = ({ request, storeToken, storeUuid, startDate, endDate, refreshSignal, onSyncingChange }) => {
  const [granularity, setGranularity] = useState('DAILY');
  const hook = useAbandonmentAnalytics({ request, storeToken, storeUuid, startDate, endDate, granularity, refreshSignal });
  const summary = hook.data?.summary || null;
  const bySection = hook.data?.bySection || null;
  const trend = hook.data?.trend || null;
  const syncing = hook.isLoading || hook.isRefreshing;

  const isEmpty = useMemo(() => !summary && !bySection && !trend, [bySection, summary, trend]);
  const histogram = useMemo(() => toAbandonmentHistogramSeries(summary), [summary]);
  const bySectionSeries = useMemo(() => toAbandonmentBySectionSeries(bySection), [bySection]);
  const trendSeries = useMemo(() => toAbandonmentTrendSeries(trend), [trend]);

  useEffect(() => {
    if (typeof onSyncingChange !== 'function') return () => null;
    onSyncingChange(syncing);
    return () => onSyncingChange(false);
  }, [onSyncingChange, syncing]);

  if (hook.isLoading) return <LoadingSkeleton />;
  if (hook.isError) return <ErrorState onRetry={hook.refetch} />;
  if (isEmpty) return <EmptyState message="No abandonment analytics data for the selected period." />;

  return (
    <div className="analytics-page-stack">
      <div className="analytics-page-grid-2">
        <AbandonmentKPIs summary={summary || {}} />
        <GranularitySelector value={granularity} options={GRANULARITIES} onChange={setGranularity} />
      </div>
      <div className="analytics-page-grid-2">
        <AbandonmentTimingHistogram data={histogram} />
        <AbandonmentBySectionChart data={bySectionSeries} />
      </div>
      <AbandonmentTrendChart data={trendSeries} />
    </div>
  );
};

export default AbandonmentAnalyticsPage;
